import { t } from "@lingui/core/macro"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft, Save } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import type { FlagWithRelations } from "@/features/feature-flags/types/flagTypes"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { flagDetailQuery } from "@/features/feature-flags/components/FlagDetail"
import { FlagStatusBadge } from "@/features/feature-flags/components/FlagStatusBadge"
import { getFlagFn, updateFlagFn } from "@/features/feature-flags/server/flagManagement"
import { flagQueryKeys } from "@/lib/queryKeys"

const Params = z.object({
  flagId: z.string(),
})

export const Route = createFileRoute("/(authenticated)/settings/feature-flags/$flagId/edit")({
  params: Params,
  loader: ({ context, params }) => {
    void context.queryClient.ensureQueryData(flagDetailQuery(params.flagId))
  },
  component: EditFlagPage,
})

function EditFlagPage() {
  const { flagId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: flag } = useSuspenseQuery({
    queryKey: flagQueryKeys.detail(flagId),
    queryFn: async () => {
      const result = await getFlagFn({ data: { flagId } })
      return result as FlagWithRelations
    },
  })

  const [name, setName] = useState(flag.name)
  const [description, setDescription] = useState(flag.description ?? "")

  const updateMutation = useMutation({
    mutationFn: updateFlagFn,
    onSuccess: () => {
      toast.success(t`Flag updated`)
      void queryClient.invalidateQueries({ queryKey: flagQueryKeys.detail(flagId) })
      void navigate({
        to: "/settings/feature-flags/$flagId",
        params: { flagId },
      })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t`Failed to update flag`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    updateMutation.mutate({
      data: {
        flagId,
        name,
        description: description || null,
      },
    })
  }

  const isValid = name.length >= 1
  const hasChanges = name !== flag.name || description !== (flag.description ?? "")
  const isArchived = !!flag.archivedAt

  return (
    <div className="flex-1 space-y-6 overflow-y-auto pb-8">
      <div>
        <Link
          to="/settings/feature-flags/$flagId"
          params={{ flagId }}
          className="mb-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t`Back to Flag Details`}
        </Link>
        <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
          {t`Feature Flag`}
        </div>
        <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
          {t`Edit Flag`}
        </h1>
        <div className="flex items-center gap-4">
          <code className="rounded bg-neutral-100 px-2 py-1 font-mono text-sm dark:bg-neutral-800">
            {flag.key}
          </code>
          <Badge variant="outline">{flag.valueType}</Badge>
          {isArchived && <FlagStatusBadge enabled={false} archived />}
        </div>
      </div>

      {isArchived && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
          <p className="text-amber-800 dark:text-amber-200">
            {t`This flag is archived and cannot be edited.`}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t`Flag Metadata`}</CardTitle>
            <CardDescription>
              {t`Update the display name and description for this flag`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="key">{t`Key`}</Label>
              <Input
                id="key"
                value={flag.key}
                disabled
                className="font-mono bg-neutral-50 dark:bg-neutral-900"
              />
              <p className="text-sm text-neutral-500">
                {t`Flag keys cannot be changed after creation`}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t`Name`}</Label>
              <Input
                id="name"
                placeholder="My Feature Flag"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isArchived}
              />
              <p className="text-sm text-neutral-500">{t`Human-readable name for the flag`}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t`Description`}</Label>
              <Textarea
                id="description"
                placeholder="Describe what this flag controls..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isArchived}
              />
              <p className="text-sm text-neutral-500">
                {t`Optional description of what this flag controls`}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t`Value Type`}</Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{flag.valueType}</Badge>
                <span className="text-sm text-neutral-500">
                  {t`Value type cannot be changed after creation`}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: "/settings/feature-flags/$flagId",
                    params: { flagId },
                  })
                }
              >
                {t`Cancel`}
              </Button>
              <Button
                type="submit"
                disabled={!isValid || !hasChanges || updateMutation.isPending || isArchived}
              >
                <Save className="mr-2 size-4" />
                {updateMutation.isPending ? t`Saving...` : t`Save Changes`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
